import {
  type ILogObjMeta,
  Logger,
  type ILogObj,
  type ISettingsParam,
} from 'tslog'

// create an enum for log levels
export enum LogLevel {
  TRACE = 'TRACE',
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  FATAL = 'FATAL',
}

export interface ThymeLoggerConfig {
  env: string
}

/**
 * Type alias for the log method return type
 */
type LoggerReturn<LogObj> = (LogObj & ILogObjMeta) | undefined

/**
 * Type for structured log arguments
 */
type LogArguments = Record<string, unknown>

/**
 * Type for arguments that can be passed to error/fatal methods
 */
type ErrorArguments = LogArguments | Error | string

/**
 * Extends the tslog Logger to format logs for Datadog parsing
 * Usage example:
 * ```
 * const logger = new ThymeLogger({settings})
 * logger.info('message', { some_property: 'foo' })
 * logger.error('error occurred', new Error('Something went wrong'))
 * logger.error('error occurred', 'An error message string')
 * logger.error('error occurred', { error: new Error('Something went wrong') })
 * ```
 */
export class ThymeLogger<LogObj> extends Logger<LogObj> {
  /**
   * Formats error objects for logging
   * @param args - Error, string or object containing error information
   * @returns Structured log arguments
   */
  private formatErrorArgs(args?: ErrorArguments): LogArguments {
    if (!args) return {}

    let logArgs: LogArguments = {}

    if (args instanceof Error) {
      logArgs = {
        error: {
          type: args.constructor.name,
          message: args.message,
          stack: args.stack,
        },
      }
    } else if (typeof args === 'string') {
      logArgs = {
        error: {
          message: args,
        },
      }
    } else if (typeof args === 'object') {
      // Clone args to avoid mutation
      logArgs = { ...args }

      if (logArgs.error instanceof Error) {
        const err = logArgs.error
        logArgs.error = {
          type: err.constructor.name,
          message: err.message,
          stack: err.stack,
        }
      } else if (typeof logArgs.error === 'string') {
        logArgs.error = {
          message: logArgs.error,
        }
      }
    }

    return logArgs
  }

  /**
   * Log a message with TRACE level
   * @param message - The message to log
   * @param args - Optional additional data to log
   * @returns Log object or undefined
   */
  trace(message: string, args?: LogArguments): LoggerReturn<LogObj> {
    return super.trace({ message, status: LogLevel.TRACE, ...args })
  }

  /**
   * Log a message with DEBUG level
   * @param message - The message to log
   * @param args - Optional additional data to log
   * @returns Log object or undefined
   */
  debug(message: string, args?: LogArguments): LoggerReturn<LogObj> {
    return super.debug({ message, status: LogLevel.DEBUG, ...args })
  }

  /**
   * Log a message with INFO level
   * @param message - The message to log
   * @param args - Optional additional data to log
   * @returns Log object or undefined
   */
  info(message: string, args?: LogArguments): LoggerReturn<LogObj> {
    return super.info({ message, status: LogLevel.INFO, ...args })
  }

  /**
   * Log a message with WARN level
   * @param message - The message to log
   * @param args - Optional additional data to log
   * @returns Log object or undefined
   */
  warn(message: string, args?: LogArguments): LoggerReturn<LogObj> {
    return super.warn({ message, status: LogLevel.WARN, ...args })
  }

  /**
   * Log a message with ERROR level
   * @param message - The message to log
   * @param args - Optional error object, string, or additional data to log
   * @returns Log object or undefined
   */
  error(message: string, args?: ErrorArguments): LoggerReturn<LogObj> {
    const logArgs = this.formatErrorArgs(args)
    return super.error({ message, status: LogLevel.ERROR, ...logArgs })
  }

  /**
   * Log a message with FATAL level
   * @param message - The message to log
   * @param args - Optional error object, string, or additional data to log
   * @returns Log object or undefined
   */
  fatal(message: string, args?: ErrorArguments): LoggerReturn<LogObj> {
    const logArgs = this.formatErrorArgs(args)
    return super.fatal({ message, status: LogLevel.FATAL, ...logArgs })
  }
}

const baseSettings: ISettingsParam<ILogObj> = {
  name: 'connect',
}

const localSettings: ISettingsParam<ILogObj> = {
  type: 'pretty',
}

const nonLocalSettings: ISettingsParam<ILogObj> = {
  type: 'json',
  // Recommended to avoid performance hits in production
  // tslog.js.org/#/?id=hidelogpositionforproduction-default-false
  hideLogPositionForProduction: true,
}

/**
 *
 * @param config
 */
function getEnvLoggingSettings(config: ThymeLoggerConfig): Record<string, any> {
  if (config.env === 'dev') {
    return localSettings
  }
  return nonLocalSettings
}

let logger: undefined | ThymeLogger<ILogObj>

/**
 *
 * @param config
 */
export function initializeLogger(
  config: ThymeLoggerConfig
): ThymeLogger<ILogObj> {
  logger = new ThymeLogger(
    {
      ...baseSettings,
      ...getEnvLoggingSettings(config),
    },
    { env: config.env }
  )

  logger.info('Initialized logger')
  return logger
}

/**
 * Returns the initialized logger instance.
 */
export function getLogger(): ThymeLogger<ILogObj> {
  if (logger) {
    return logger
  }
  throw new Error('Logger not initialized')
}
